import BaseModel from "lib/model";
import BaseCollection from "lib/collection";
import Warehouses from "collections/warehouses";
import fetch from "lib/fetch";
import min from "lodash/min";
import max from "lodash/max";
import me from "models/me";
import Globalize from "globalize";
import querystring from "querystring";

const PricingModel = BaseModel.extend({
    props: {
        _id: "objectId",
        __currency_code: { type: "string", required: true },
        __original_wholesale: { type: "number", required: false, default: 0 },
        __adjusted_wholesale: { type: "number", required: false, default: 0 },
        __discounted_wholesale: { type: "number", required: false, default: 0 },
        __wholesale: { type: "number", required: false, default: 0 },
        __retail: { type: "number", default: 0 },
        __disabled: { type: "boolean", default: false },
    },
});

const PricingCollection = BaseCollection.extend({
    mainIndex: "__currency_code",
    model: PricingModel,
});

const PriceModel = BaseModel.extend({
    props: {
        _id: "objectId",
        __sku_id: "objectId", // size._id
        __currency_code: { type: "string", required: true },
        __type: { type: "string", required: true }, // wholesale | retail
        __price: { type: "number", default: 0, required: true },
        __start_date: { type: "date", required: false },
        __end_date: { type: "date", required: false },
    },
});

const PricesCollection = BaseCollection.extend({
    model: PriceModel,
});

const PricesheetPriceModel = BaseModel.extend({
    props: {
        _id: "objectId",
        __sku_id: "objectId", // size._id
        __currency_code: { type: "string", required: false },
        __type: { type: "string", required: true }, // wholesale | retail
        __price: { type: "number", default: 0, required: true },
        __start_date: { type: "date", required: false },
        __end_date: { type: "date", required: false },
    },
});

const PricesheetPricesCollection = BaseCollection.extend({
    model: PricesheetPriceModel,
});

const PricesheetModel = BaseModel.extend({
    props: {
        _id: "objectId",
        __name: { type: "string", required: true },
        __currency_code: { type: "string", required: false },
    },

    collections: {
        __prices: PricesheetPricesCollection,
    },
});

const PricesheetsCollection = BaseCollection.extend({
    mainIndex: "__name",
    model: PricesheetModel,
});

const SizeModel = BaseModel.extend({
    props: {
        _id: "objectId",
        __size: { type: "string", required: true },
        __size_group: "string",
        inventory: "array",
    },

    collections: {
        __pricing: PricingCollection,
    },

    derived: {
        _name: {
            fn() {
                return this.__size.replace(this.__size_group, "");
            },
        },
    },
});

const SizeCollection = BaseCollection.extend({
    mainIndex: "__size",
    model: SizeModel,
});

const Ref = BaseModel.extend({
    props: {
        __ref: { type: "objectId", required: true },
        __name: { type: "string" },
    },
});

export default BaseModel.extend({
    props: {
        _id: "objectId",
        __schema_id: "objectId",

        __active: { type: "boolean", default: false },
        __color: { type: "string" },
        __colorBanner: { type: "string" },
        __cancelled: { type: "boolean", default: false },
        __unique_key: { type: "string" },

        __images: "array", // new api images
        //name: 'string',

        __available_from: "date",
        __available_until: "date",
        __available_now: { type: "boolean", default: false },

        __size_groups: "array",

        __banners: { type: "array" },
        __images_cache: "array",
        __price_templates: "array",

        __inventory: "array",
        __variants: "array",
        __sold_out: "boolean",
        __tracks_inventory: "boolean",
        __exempt_from_order_surcharge: "boolean",
        __pack_ids: "array",
    },

    collections: {
        __sizes: SizeCollection,
        __pricing: PricingCollection,
        __prices: PricesCollection,
        __pricesheets: PricesheetsCollection,
        __warehouses: Warehouses,
    },

    children: {
        __brand: Ref,
    },

    initialize: function () {
        this.cdn = this._id
            ? `//cdn${(this._id.charCodeAt(this._id.length - 1) % 4) + 1}.nuorder.com`
            : "//cdn1.nuorder.com";
    },

    derived: {
        _disabled: {
            cache: false,
            fn() {
                const priceTemplate = this.getPriceTemplate();
                if (priceTemplate) {
                    if (priceTemplate.__disabled) {
                        return true;
                    }
                } else {
                    const pricingEntry = this.__pricing.get(me._activeCurrency);
                    if (!pricingEntry || pricingEntry.__disabled) {
                        return true;
                    }
                }

                return false;
            },
        },
        _formattedPriceRange: {
            cache: false,
            fn() {
                return this.formatPrice(this._wholesalePriceLow, this._wholesalePriceHigh);
            },
        },
        _formattedRetailPriceRange: {
            cache: false,
            fn() {
                return this.formatPrice(this._retailPriceLow, this._retailPriceHigh);
            },
        },
        _hasWholesalePriceRange: {
            cache: false,
            fn() {
                const maxPrice = this._wholesalePriceHigh;
                const minPrice = this._wholesalePriceLow;
                if (!minPrice || !maxPrice || me.isGuestBuyer()) {
                    return false;
                }
                return minPrice !== maxPrice;
            },
        },
        _wholesalePriceHigh: {
            cache: false,
            fn() {
                return max(this._wholesaleSizePriceArray);
            },
        },
        _wholesalePriceLow: {
            cache: false,
            fn() {
                return min(this._wholesaleSizePriceArray);
            },
        },
        _retailPriceHigh: {
            cache: false,
            fn() {
                return max(this._retailSizePriceArray);
            },
        },
        _retailPriceLow: {
            cache: false,
            fn() {
                return min(this._retailSizePriceArray);
            },
        },
        _wholesaleSizePriceArray: {
            cache: false,
            fn() {
                return Object.values(this._wholesaleSizePrices);
            },
        },
        _retailSizePriceArray: {
            cache: false,
            fn() {
                return Object.values(this._retailSizePrices);
            },
        },
    },

    url() {
        return `/api/v3.0/item/${this._id}`;
    },

    addToOrder(options = {}) {
        options.item_id = this._id;
        return fetch("/api/v2.0/orders/working/line-items", {
            method: "POST",
            body: JSON.stringify(options),
        }).then((response) => {
            if (response.status >= 400) {
                return response.json().then((data) => {
                    throw new Error(data.message || Globalize.formatMessage("Error adding to cart"));
                });
            }
            return response.json();
        });
    },

    removeFromOrder() {
        return fetch(`/api/v2.0/orders/working/line-items?${querystring.stringify({ product: this._id })}`, {
            method: "DELETE",
        }).then((response) => {
            if (response.status >= 400) {
                return response.json().then((data) => {
                    throw new Error(data.message || Globalize.formatMessage("Error adding to cart"));
                });
            }
            return response.json();
        });
    },

    formatPrice(minPrice, maxPrice, currency = me._activeCurrency) {
        if (!minPrice || !maxPrice) {
            return "";
        }

        let range = Globalize.formatCurrency(minPrice, currency);
        if (minPrice !== maxPrice) {
            range = range + " - " + Globalize.formatCurrency(maxPrice, currency);
        }

        return range;
    },

    getColorVariants() {
        const colorVariants = this.__variants.find((variant) => variant.key === "color");
        return colorVariants ? colorVariants.items : [];
    },

    getVariants() {
        if (this._variants) {
            // cached
            return Promise.resolve(this._variants);
        } else if (!this.style_number) {
            // cant have color variant
            return Promise.resolve();
        } else {
            const variants = new this.constructor.ItemCollection();
            const options = {
                reset: true,
                data: {
                    __fields: "__all", // remove in the future
                    __limit: 1000, // just in case the API has a default, lets make this bigger
                    style_number: this.style_number,
                    __populate: "__variant",
                },
            };
            this.varientPromise = variants.fetchByPromise(options).then(() => {
                this._variants = variants;
                // Sub out child of same variant with this parent
                this._variants.some((variant, i) => {
                    if (variant._id === this._id) {
                        this._variants.remove(variant);
                        this._variants.add(this, { at: i });
                        return true;
                    }
                    return false;
                });
                return this._variants;
            });
            return this.varientPromise;
        }
    },

    getPriceTemplate() {
        return this.ensureTemplateIndexes()[me._activePriceTemplate];
    },

    ensureTemplateIndexes() {
        return this.templateIndexes === undefined && this.__price_templates
            ? this.buildTemplateIndexes()
            : this.templateIndexes;
    },

    buildTemplateIndexes() {
        this.templateIndexes = this.__price_templates.reduce((indexes, priceTemplate) => {
            indexes[priceTemplate.__template] = priceTemplate;
            return indexes;
        }, {});

        return this.templateIndexes;
    },

    getPricesheet() {
        return this.ensurePricesheetIndexes()[me._activePriceTemplate];
    },

    ensurePricesheetIndexes() {
        return this.pricesheetIndexes === undefined && this.__pricesheets
            ? this.buildPricesheetIndexes()
            : this.pricesheetIndexes;
    },

    buildPricesheetIndexes() {
        this.pricesheetIndexes = this.__pricesheets.reduce((indexes, pricesheet) => {
            indexes[pricesheet.__name] = pricesheet;
            return indexes;
        }, {});

        return this.pricesheetIndexes;
    },

    getImageSrc(index = 0, size = "G", scope) {
        // If first image isnt there, we always want to return a filler
        if (index === 0 && (!this.__images || this.__images.length === 0)) {
            return `${this.cdn}/brands/${scope.brand.settings.missing_logo_image}.jpg`;
        }

        if (!this.__images || !this.__images[index]) {
            return null;
        }
        return `${this.cdn}/product/${this.__images[index]}${size}.jpg`;
    },

    getImagesAndSwatch(scope, size) {
        const swatch = scope.brand.modules.base.color_swatches.filter(
            (swatch) => swatch.color === this.color && swatch.swatch_type === "file",
        );
        const imageUrlArry = this.__images.map((image, i) => this.getImageSrc(i, size, scope));
        const swatchUrl = `//cdn1.nuorder.com/brand_color_swatch/${swatch[0].value}.jpg`;
        imageUrlArry.push(swatchUrl);
        return imageUrlArry;
    },
});
